import { useState, useEffect } from 'react';
import { ApiClient } from '../api/client';
import WebApp from '@twa-dev/sdk';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  activated: boolean;
  balance: number;
}

interface UsePaymentProps {
  onPaymentComplete: (updatedUserInfo: UserInfo) => void;
}

type PaymentType = 'system_access' | 'balance';

interface PaymentRequest {
  title: string;
  description: string;
  currency: string;
  amount: number;
  payment_type: 'SYSTEM_ACCESS' | 'BALANCE_TOPUP';
}

interface PaymentResponse {
  payment_id: string;
  invoice_url: string;
}

export function usePayment({ onPaymentComplete }: UsePaymentProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Cleanup interval on unmount
    return () => {
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
    };
  }, [paymentCheckInterval]);

  const handlePayment = async (type: PaymentType, amount?: number) => {
    setIsLoading(true);
    try {
      const tg = WebApp;
      let response: PaymentResponse;

      if (type === 'system_access') {
        response = await ApiClient.purchaseSystemAccess();
      } else {
        // Balance payment
        if (!amount) throw new Error('Amount is required for balance payment');
        
        const request: PaymentRequest = {
          title: `${amount} Telegram Stars`,
          description: `Add ${amount} sparkles to your balance`,
          currency: 'XTR',
          amount: amount,
          payment_type: 'BALANCE_TOPUP'
        };
        
        response = await ApiClient.createPayment(request);
      }
      
      try {
        await tg.openInvoice(response.invoice_url);
      } catch (error) {
        console.error('Failed to open invoice:', error);
        setIsLoading(false);
        return;
      }
      
      // Clear any existing interval
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
      
      let attempts = 0;
      const maxAttempts = 30;
      
      const interval = setInterval(async () => {
        try {
          const payment = await ApiClient.getPaymentStatus(response.payment_id);
          
          if (payment.status === 'COMPLETED') {
            clearInterval(interval);
            const updatedUserInfo = await ApiClient.getUserInfo();
            onPaymentComplete(updatedUserInfo);
            setIsLoading(false);
          } else if (payment.status === 'FAILED' || attempts >= maxAttempts) {
            clearInterval(interval);
            setIsLoading(false);
          }
          
          attempts++;
        } catch (error) {
          console.error('Failed to check payment status:', error);
          clearInterval(interval);
          setIsLoading(false);
        }
      }, 2000);

      setPaymentCheckInterval(interval);
    } catch (error) {
      console.error('Payment failed:', error);
      setIsLoading(false);
    }
  };

  const handlePremiumClick = () => handlePayment('system_access');
  const handleBalancePayment = (amount: number) => handlePayment('balance', amount);

  return {
    isLoading,
    handlePremiumClick,
    handleBalancePayment
  };
} 